import { useQuery } from '@tanstack/react-query';
import { getSupabaseClient } from '../utils/supabaseClient';
import { WORK_ORDER_STATUS } from '../constants/statusEnums';
import { getCustomerIdsForCompany } from '../utils/customerRelationshipUtils';
import { buildCustomerSearchFilter } from '../utils/customerSearch';

/**
 * Names that exist in customer_tags but ARE the customer's type, not a tag about them.
 * One classification, one home: customers.customer_type / customers.type.
 */
export const TYPE_PSEUDO_TAGS = new Set(['residential', 'commercial', 'industrial', 'government']);

const SERVER_PAGE_SIZE = 20;

/**
 * fetchCustomersPaginated - Replicates loadCustomers from Customers.js
 * Uses company_customers relationship table for fetching customers
 * Falls back to legacy company_id if relationship table is empty
 */
const fetchCustomersPaginated = async (companyId, page, pageSize, search = '', fetchAll = false, includeArchived = false) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();
  const serverPage = page - 1; // Convert 1-based to 0-based
  const from = serverPage * pageSize;
  const to = from + pageSize - 1;

  // ONE request. This was three in a row: every company_customers id, then the archived ids
  // among them, then this query with id=in.(…the rest…). The junction is now joined here (the
  // empty embed `company_customers!inner()` only filters, it adds no field to the rows) and the
  // archived exclusion is a filter on the same query. Same rows, count, columns and order
  // (page speed Phase 2, 2026-10-25; tests/e2e/speed/customer-join-equivalence.spec.js).
  let query = supabase
    .from('customers')
    .select(
      `
      id, name, first_name, last_name, company_name, email, phone, mobile_phone, preferred_contact, reminders_opt_out, street_address, city, state, zip_code, billing_address_line_1, billing_address_line_2, billing_city, billing_state, billing_zip_code, billing_country, company_id, created_at, notes, is_active, status, rating, customer_since, updated_at, credit_limit, payment_terms, tax_exempt, source, customer_type, has_portal_account, type,
      customer_tag_assignments(
        tag_id,
        customer_tags(
          id,
          name,
          color,
          description
        )
      ),
      company_customers!inner()
    `,
      { count: 'exact' }
    )
    .eq('company_customers.company_id', companyId);

  // Archived customers leave the working list (board #2514) — and that has to happen HERE, on
  // the server side, not only in the render filter. Dropping them after the fetch meant the
  // exact count, the page's 25 rows and every tile still included them: page 1 rendered 21 of
  // 25 rows, the header said 146 while Export wrote 142, the New (30d) tile disagreed with its
  // own filter by exactly the archived count, and sorting "dropped" customers (board #4193
  // #4194 #4316 #4317 #4320). Written as "NULL or not archived" so a NULL status is never
  // excluded by accident (a bare neq drops NULLs) — the same rows the old by-id removal kept.
  if (!includeArchived) {
    query = query.or('status.is.null,status.neq.archived');
  }

  // ✅ Server-side search across ALL of the company's customers (not just the loaded page).
  // Board #207: this searched no address column at all, and stripped `()` out of the term
  // before an ilike — so "(541) 978-9236", the exact format the app DISPLAYS, matched nothing
  // while the bare digits worked. The rule now lives in utils/customerSearch, shared with the
  // client predicate in pages/Customers.js so the two cannot disagree about what a row is.
  // One .or() per search word; PostgREST ANDs them, so every word must match (#5640).
  for (const wordFilter of buildCustomerSearchFilter(search) || []) {
    query = query.or(wordFilter);
  }

  // `fetchAll` deliberately skips .range(): every filter on the Customers page runs
  // CLIENT-side, so a filtered view must be given every candidate row or it silently
  // filters a 20-row window. Measured live 2026-08-28 on 48f32d34 — the "Owes Money" tile
  // counted 8 customers company-wide and all 8 sorted older than the newest 20, so
  // clicking it produced an EMPTY table. See `filterActive` in pages/Customers.js.
  const ordered = query.order('created_at', { ascending: false });
  const { data, error, count } = await (fetchAll ? ordered : ordered.range(from, to));

  if (error) {
    throw error;
  }

  const normalized = (data || []).map((c) => {
    // Extract tags from the proper relationship
    // Legacy type pseudo-tags are excluded: they are not things a contractor tagged a
    // customer WITH, they are the classification duplicated into the tag table, and showing
    // them alongside real tags is what made Type and Tags look like two competing answers.
    const tags = (c.customer_tag_assignments || [])
      .map((assignment) => assignment.customer_tags?.name)
      .filter(Boolean)
      .filter((name) => !TYPE_PSEUDO_TAGS.has(String(name).toLowerCase()));

    // CUSTOMER TYPE COMES FROM THE COLUMNS, NOT FROM A TAG.
    //
    // This used to read a tag FIRST — any assignment named residential/commercial/industrial
    // outranked both type columns. Live 2026-09-16 those three names account for 231 of 232
    // tag assignments, so for most customers a tag was deciding their type. Change the radio
    // from Residential to Commercial, save, reload, and the tag put it straight back: reported
    // as Edit silently dropping customer_type, and as editing a residential customer to
    // commercial "changing the schema".
    //
    // `customer_type` first, then `type`. `type` is the CHECK-constrained column and is
    // populated on every row; `customer_type` is what every reader on this page already
    // displays. The fallback covers the rows created before the create form wrote both.
    const customerType = c.customer_type || c.type || 'residential';

    // Create unified name field for display
    const displayName =
      c.display_name ||
      (c.company_name && c.company_name.trim()) ||
      (c.first_name && c.last_name ? `${c.first_name} ${c.last_name}`.trim() : null) ||
      (c.first_name && c.first_name.trim()) ||
      (c.last_name && c.last_name.trim()) ||
      'Unnamed Customer';

    return {
      ...c,
      name: displayName,
      status: (c.status || 'active').toString().toLowerCase(),
      is_active: c.is_active !== false, // Preserve boolean, default true
      rating: c.rating ? parseFloat(c.rating) : 0, // Parse string "5.00" → number
      tags: tags,
      customer_type: (customerType || 'residential').toLowerCase(),
      relationship_type: 'client',
      relationship_status: 'active',
      has_portal_account: !!c.has_portal_account,
      role: 'customer',
      customer_since: c.customer_since || c.created_at,
    };
  });

  return { customers: normalized, totalCount: count || 0 };
};

/**
 * React Query hook for paginated customers with tags
 */
export const useCustomersPaginated = (companyId, page = 1, pageSize = SERVER_PAGE_SIZE, search = '', fetchAll = false, includeArchived = false) => {
  return useQuery({
    // `fetchAll` is part of the key: the filtered and unfiltered fetches return different
    // row sets and must not share a cache entry.
    queryKey: ['customers-page', companyId, page, pageSize, search, fetchAll, includeArchived],
    queryFn: () => fetchCustomersPaginated(companyId, page, pageSize, search, fetchAll, includeArchived),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId,
  });
};

/**
 * Lightweight all-customers fetch for KPI calculations (no pagination)
 */
const fetchAllCustomersForKPI = async (companyId) => {
  if (!companyId) throw new Error('Company ID is required');
  const supabase = getSupabaseClient();
  
  // Junction joined in the same request (empty embed: filters only, no extra field) — page
  // speed Phase 2, 2026-10-25, see fetchCustomersPaginated.
  const { data, error } = await supabase
    .from('customers')
    .select('id, is_active, status, rating, created_at, customer_since, updated_at, credit_limit, company_customers!inner()')
    .eq('company_customers.company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map(c => ({
    ...c,
    is_active: c.is_active !== false,
    rating: c.rating ? parseFloat(c.rating) : 0,
    status: (c.status || 'active').toString().toLowerCase(),
  }));
};

/**
 * Every customer in the company, WITH the identifying fields — for the CSV export.
 *
 * Not a hook and not cached: it runs when someone presses Export, which is a deliberate
 * action a few times a day, and it must return the truth at that moment.
 *
 * WHY IT EXISTS. The export used to map `allCustomersForKPI`, which was itself a fix — the
 * export had been running off the 20-row page and reporting 20 of 67 as the total. But that
 * array is a deliberately LIGHTWEIGHT projection for the KPI tiles: it selects
 * `id, is_active, status, rating, created_at, customer_since, updated_at, credit_limit` and
 * nothing else. So every identifying column in the CSV — first name, last name, email, phone,
 * address, tags — resolved to `undefined` and was written as an empty string, while the
 * columns that happened to be in that select (rating, status) and the ones computed from
 * analytics (jobs, revenue) filled in correctly. That is exactly the pattern the operator
 * saw across all 69 rows, and it is why the file looked half-populated rather than broken.
 * `Type` looked fine and was not: `customer_type` is absent too, so every row took the
 * 'residential' fallback.
 *
 * Widening the KPI select instead would have made every page load carry ten more columns for
 * all users to serve an occasional download.
 */
export const fetchCustomersForExport = async (companyId) => {
  if (!companyId) return [];
  const supabase = getSupabaseClient();

  const customerIds = await getCustomerIdsForCompany(companyId);
  if (customerIds.length === 0) return [];

  const { data, error } = await supabase
    .from('customers')
    .select(`
      id, first_name, last_name, company_name, email, phone, mobile_phone,
      street_address, city, state, zip_code,
      billing_address_line_1, billing_city, billing_state, billing_zip_code,
      customer_type, type, rating, status, is_active, created_at, customer_since,
      customer_tag_assignments(customer_tags(name))
    `)
    .in('id', customerIds);

  if (error) throw error;

  // Same reader and the same tag exclusion the PAGE uses. The export had its own idea of
  // both, so a customer could be exported with Type "residential" and a Tag column saying
  // "commercial" — two columns of one CSV disagreeing about one customer.
  return (data || []).map((c) => ({
    ...c,
    customer_type: (c.customer_type || c.type || 'residential').toLowerCase(),
    rating: c.rating ? parseFloat(c.rating) : null,
    status: (c.status || 'active').toString().toLowerCase(),
    is_active: c.is_active !== false,
    tags: (c.customer_tag_assignments || [])
      .map((a) => a.customer_tags?.name)
      .filter(Boolean)
      .filter((name) => !TYPE_PSEUDO_TAGS.has(String(name).toLowerCase())),
  }));
};

export const useAllCustomersForKPI = (companyId) => {
  return useQuery({
    queryKey: ['customers-all-kpi', companyId],
    queryFn: () => fetchAllCustomersForKPI(companyId),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });
};

/**
 * Per-customer totals for the Customers page, computed in the database (PRODUCT_DECISIONS
 * "Data efficiency" ruling 3, 2026-11-02): one jsonb object keyed by customer id, from
 * get_customer_page_aggregates. The page used to download every job, quote, invoice and
 * communication of the company (four unlimited selects, each capped at 1,000 rows by
 * PostgREST) and sum them in the browser. Field meanings: the header of
 * supabase/migrations/20261102_customer_page_aggregates.sql; read them through
 * utils/customerAggregates.js.
 */
const fetchCustomerAggregates = async (companyId) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('get_customer_page_aggregates', { _company_id: companyId });
  if (error) throw error;
  return data || {};
};

export const useCustomerAggregates = (companyId) => {
  return useQuery({
    queryKey: ['customer-aggregates', companyId],
    queryFn: () => fetchCustomerAggregates(companyId),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });
};

/**
 * fetchCustomerJobs - ONE customer's jobs, for the profile modal (loaded when it opens).
 * Queries work_orders with broad status filter
 */
const fetchCustomerJobs = async (companyId, customerId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  // Not select('*'): the columns any code on the Customers page can name (page speed Phase 2,
  // 2026-10-25) — every word in pages/Customers.js and its transitive local imports that equals a
  // live work_orders column. select=* sent 2.0 MB of jobs and 0.75 MB of quotes for Jerry's. A NEW reader of these rows on the
  // Customers page (list, KPI tiles, filters, profile, export analytics) must add its column here.
  const { data, error } = await supabase
    .from('work_orders')
    .select('actual_end,assigned_technician_id,company_id,completed_at,created_at,created_by,customer_address_id,customer_equipment_id,customer_feedback,customer_id,deposit_paid_at,deposit_required_before_scheduling,deposit_type,description,due_date,expiration_date,id,invoice_date,notes,paid_at,payment_terms,portal_token,priority,quote_accepted_at,quote_number,scheduled_end,scheduled_start,service_address_line_1,service_address_line_2,service_city,service_date,service_state,service_zip_code,source,status,tax_exempt,tax_rate,title,total_amount,updated_at,warranty_info,work_order_number')
    .eq('company_id', companyId)
    .eq('customer_id', customerId)
    .in('status', [
      WORK_ORDER_STATUS.SCHEDULED,
      WORK_ORDER_STATUS.IN_PROGRESS,
      WORK_ORDER_STATUS.COMPLETED,
      WORK_ORDER_STATUS.CANCELLED,
      WORK_ORDER_STATUS.INVOICED,
      WORK_ORDER_STATUS.PAID,
    ])
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};

/**
 * React Query hook for customer jobs
 */
export const useCustomerJobs = (companyId, customerId) => {
  return useQuery({
    queryKey: ['customer-jobs', companyId, customerId],
    queryFn: () => fetchCustomerJobs(companyId, customerId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId && !!customerId,
  });
};

/**
 * fetchCustomerQuotes - ONE customer's quotes, for the profile modal.
 * Queries work_orders with quote statuses
 */
const fetchCustomerQuotes = async (companyId, customerId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  // Same column list as fetchCustomerJobs (same page, same readers).
  const { data, error } = await supabase
    .from('work_orders')
    .select('actual_end,assigned_technician_id,company_id,completed_at,created_at,created_by,customer_address_id,customer_equipment_id,customer_feedback,customer_id,deposit_paid_at,deposit_required_before_scheduling,deposit_type,description,due_date,expiration_date,id,invoice_date,notes,paid_at,payment_terms,portal_token,priority,quote_accepted_at,quote_number,scheduled_end,scheduled_start,service_address_line_1,service_address_line_2,service_city,service_date,service_state,service_zip_code,source,status,tax_exempt,tax_rate,title,total_amount,updated_at,warranty_info,work_order_number')
    .eq('company_id', companyId)
    .eq('customer_id', customerId)
    .in('status', [WORK_ORDER_STATUS.DRAFT, WORK_ORDER_STATUS.SENT, WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.REJECTED])
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};

/**
 * React Query hook for customer quotes
 */
export const useCustomerQuotes = (companyId, customerId) => {
  return useQuery({
    queryKey: ['customer-quotes', companyId, customerId],
    queryFn: () => fetchCustomerQuotes(companyId, customerId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId && !!customerId,
  });
};

/**
 * fetchCustomerInvoices - ONE customer's invoices, for the profile modal.
 * Queries invoices table (filtered by company_id and customer_id)
 */
const fetchCustomerInvoices = async (companyId, customerId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  // Not select('*'): the columns any code on the Customers page can name (page speed Phase 2,
  // 2026-10-25) — every word in pages/Customers.js and its transitive local imports that equals a
  // live invoices column. select=* sent 163 KB for Jerry's. A NEW reader of these rows on the
  // Customers page (list, KPI tiles, filters, profile, export analytics) must add its column here.
  const { data, error } = await supabase
    .from('invoices')
    .select('amount_paid,balance_due,company_id,created_at,customer_id,due_date,id,invoice_number,issue_date,kind,notes,paid_at,status,terms,total_amount,updated_at,work_order_id')
    .eq('company_id', companyId)
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};

/**
 * React Query hook for customer invoices
 */
export const useCustomerInvoices = (companyId, customerId) => {
  return useQuery({
    queryKey: ['customer-invoices', companyId, customerId],
    queryFn: () => fetchCustomerInvoices(companyId, customerId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId && !!customerId,
  });
};

/**
 * fetchCustomerCommunications - ONE customer's communications, for the profile modal.
 * Queries customer_communications with user join
 */
const fetchCustomerCommunications = async (companyId, customerId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('customer_communications')
    .select('*, created_by_user:users!created_by(first_name,last_name)')
    .eq('company_id', companyId)
    .eq('customer_id', customerId)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};

/**
 * React Query hook for customer communications
 */
export const useCustomerCommunications = (companyId, customerId) => {
  return useQuery({
    queryKey: ['customer-communications', companyId, customerId],
    queryFn: () => fetchCustomerCommunications(companyId, customerId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId && !!customerId,
  });
};

/**
 * fetchCustomerTags - Replicates loadCustomerTags from Customers.js
 * Queries customer_tags ordered by name
 */
const fetchCustomerTags = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('customer_tags')
    .select('*')
    .eq('company_id', companyId)
    .order('name', { ascending: true });

  if (error) {
    throw error;
  }

  return data || [];
};

/**
 * React Query hook for customer tags
 */
export const useCustomerTags = (companyId) => {
  return useQuery({
    queryKey: ['customer-tags', companyId],
    queryFn: () => fetchCustomerTags(companyId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId,
  });
};

/**
 * fetchServiceAgreements - Replicates loadServiceAgreements from Customers.js
 * Queries service_agreements (filtered by company_id)
 */
const fetchServiceAgreements = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('service_agreements')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return data || [];
};

/**
 * React Query hook for service agreements
 */
export const useServiceAgreements = (companyId) => {
  return useQuery({
    queryKey: ['service-agreements', companyId],
    queryFn: () => fetchServiceAgreements(companyId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId,
  });
};
